import { useState, type FormEvent } from "react";
import { getCollection, getSchema, waitForLatestTemplate } from "../api/atomicApi";
import { createPlainNftBundle, createBackedTemplateBundle, registerRateAndDeposit } from "../api/actions";
import { logActivity } from "../api/activityLog";
import { toStringAttributeMap } from "../utils/attributes";
import { useWallet } from "../wallet/WalletContext";
import { WAXCREATOR_SCHEMA_NAME, contracts } from "../config";
import { ImageUploadField } from "../components/ImageUploadField";
import { showSuccess, showError, showWarning } from "../components/Dialog";

interface AttributeRow {
  key: string;
  value: string;
}

/**
 * The main "create a custom NFT" flow: one form, upload/describe your NFT,
 * optionally back it with WAX/EOS, hit Create. Behind the scenes this
 * works out whether you already have a collection/schema set up (your
 * WAX account name doubles as your collection name, so every account gets
 * exactly one, created automatically the first time you use this) and
 * bundles whatever's missing into the same signed transaction as the
 * actual mint.
 *
 * Plain (unbacked) NFTs mint directly with no template (one unique item
 * per submission — see Advanced Create for multi-copy editions via a real
 * template). Backed NFTs need a template either way (escrowbacker's rates
 * are keyed by template_id), so that path is two signed transactions: one
 * creates the template, then this page looks up the template_id AtomicHub
 * assigned it and uses that to register the backing rate and make the
 * first deposit/mint in a second transaction. See atomicApi.ts's
 * `waitForLatestTemplate` for the caveat on that lookup — it polls the
 * AtomicHub index rather than reading the id directly out of the first
 * transaction's trace, since this sandbox couldn't verify the exact trace
 * shape WharfKit returns without a live chain to test against.
 */
export function CreateNft() {
  const { session, connect, connecting, account } = useWallet();
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [attributes, setAttributes] = useState<AttributeRow[]>([{ key: "", value: "" }]);

  const [backed, setBacked] = useState(false);
  const [backingAmount, setBackingAmount] = useState("");
  const [backingSymbol, setBackingSymbol] = useState<"WAX" | "EOS">("WAX");
  const [maxSupply, setMaxSupply] = useState("0");

  function updateAttribute(index: number, field: "key" | "value", value: string) {
    setAttributes((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  }

  function addAttributeRow() {
    setAttributes((rows) => [...rows, { key: "", value: "" }]);
  }

  function removeAttributeRow(index: number) {
    setAttributes((rows) => rows.filter((_, i) => i !== index));
  }

  async function ensureSession() {
    if (session) return session;
    await connect();
    return undefined;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!image.trim()) {
      showWarning("Add an image first — upload a file or paste an IPFS hash/URL.", "Image required");
      return;
    }
    const s = await ensureSession();
    if (!s) return;
    const actor = s.actor.toString();
    setResult(null);
    setBusy(backed ? "backed" : "plain");

    try {
      // Your WAX account name doubles as your collection name — it's
      // already a valid, globally-unique AtomicAssets collection_name
      // format, and `createcol` requires the author's own signature
      // anyway, so this needs no separate "pick a collection name" step
      // for a first-time creator. (In the rare case someone else already
      // grabbed a collection with your exact account name before you did,
      // this will fail with a clear "already exists" error — use Advanced
      // Create to pick a different name if that happens to you.)
      const collectionName = actor;

      const existingCollection = await getCollection(collectionName);
      const needsCollection = !existingCollection;
      const existingSchema = existingCollection ? await getSchema(collectionName, WAXCREATOR_SCHEMA_NAME) : null;
      const needsSchema = !existingSchema;

      const attributesJson = JSON.stringify(
        Object.fromEntries(attributes.filter((a) => a.key.trim()).map((a) => [a.key.trim(), a.value]))
      );
      const immutableData = toStringAttributeMap({
        name,
        img: image,
        description,
        attributes: attributesJson,
      });
      const collectionData = toStringAttributeMap({
        name: `${actor}'s collection`,
        description: "Created with WAXCreator (waxcreator.com)",
        img: "",
      });

      if (!backed) {
        await createPlainNftBundle(s, {
          collectionName,
          collectionData,
          needs: { needsCollection, needsSchema, needsEscrowAuth: false, needsEscrowNotify: false },
          owner: actor,
          immutableData,
        });
        logActivity({ type: "mint_plain", account: actor, collectionName, note: name });
        setResult("NFT minted — check My Assets in a moment.");
        return;
      }

      const needsEscrowAuth =
        !existingCollection || !existingCollection.authorized_accounts.includes(contracts.escrow);
      const needsEscrowNotify =
        !existingCollection || !existingCollection.notify_accounts.includes(contracts.escrow);

      await createBackedTemplateBundle(s, {
        collectionName,
        collectionData,
        needs: { needsCollection, needsSchema, needsEscrowAuth, needsEscrowNotify },
        maxSupply: Number(maxSupply),
        immutableData,
      });

      setResult("Template created — looking up its template_id to finish backing it…");
      const template = await waitForLatestTemplate(collectionName, WAXCREATOR_SCHEMA_NAME);
      if (!template) {
        setResult(
          "Template created, but couldn't confirm its template_id from the AtomicHub API yet (indexer lag). " +
            "Find it on AtomicHub or in the transaction trace, then use Advanced Create's collection/schema " +
            "fields plus the Mint page to register the rate and deposit manually — nothing about your on-chain " +
            "template is lost, this is just the lookup step catching up."
        );
        return;
      }

      await registerRateAndDeposit(s, {
        templateId: Number(template.template_id),
        collectionName,
        backing: { amount: backingAmount, symbol: backingSymbol },
      });
      logActivity({
        type: "mint_backed_create",
        account: actor,
        collectionName,
        templateId: template.template_id,
        amount: `${backingAmount} ${backingSymbol}`,
        note: name,
      });
      setResult(`Backed NFT created and minted (template #${template.template_id}) — check My Assets in a moment.`);
    } catch (err) {
      showError(err, "Failed to create NFT");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page page--narrow">
      <div className="page__header">
        <h1>Create an NFT</h1>
        <p className="page__subtitle">
          Log in, describe your NFT, optionally back it with WAX or EOS, and mint it — WAXCreator sets up your
          collection and schema automatically the first time you use this. Need multiple copies of the same item,
          or full control over your collection's structure? Use{" "}
          <a href="/create-advanced">Advanced Create</a> instead. Want it on Polygon, paid in POL?{" "}
          <a href="/create/polygon">Create on Polygon</a>.
        </p>
      </div>

      <form className="mint-form" onSubmit={handleSubmit}>
        <ImageUploadField label="Image" value={image} onChange={setImage} />

        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label>
          Description
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </label>

        <div className="mint-form__attributes">
          <h2>Attributes (optional)</h2>
          {attributes.map((row, i) => (
            <div className="mint-form__amount-row" key={i}>
              <input
                placeholder="Trait name (e.g. Rarity)"
                value={row.key}
                onChange={(e) => updateAttribute(i, "key", e.target.value)}
              />
              <input
                placeholder="Value (e.g. Legendary)"
                value={row.value}
                onChange={(e) => updateAttribute(i, "value", e.target.value)}
              />
              <button type="button" className="btn btn--outline" onClick={() => removeAttributeRow(i)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn--outline" onClick={addAttributeRow}>
            Add attribute
          </button>
        </div>

        <label>
          <input type="checkbox" checked={backed} onChange={(e) => setBacked(e.target.checked)} /> Back this NFT
          with WAX or EOS (locked in an escrow contract, released automatically if the owner ever burns it)
        </label>

        {backed && (
          <>
            <div className="mint-form__amount-row">
              <label>
                Backing amount
                <input
                  type="number"
                  min="0"
                  step="0.00000001"
                  value={backingAmount}
                  onChange={(e) => setBackingAmount(e.target.value)}
                  required={backed}
                />
              </label>
              <label>
                Token
                <select value={backingSymbol} onChange={(e) => setBackingSymbol(e.target.value as "WAX" | "EOS")}>
                  <option value="WAX">WAX</option>
                  <option value="EOS">EOS</option>
                </select>
              </label>
            </div>
            <label>
              Max copies of this backed NFT (0 = unlimited — each copy needs its own matching deposit to mint)
              <input type="number" min="0" value={maxSupply} onChange={(e) => setMaxSupply(e.target.value)} />
            </label>
            <p className="page__subtitle">
              You'll sign two transactions for a backed NFT: one creates the template, then — once we can confirm
              its template_id — a second registers the backing rate and makes the deposit that mints your first
              copy. This is a self-serve registration: no one has to approve it but you.
            </p>
          </>
        )}

        <button className="btn btn--primary" type="submit" disabled={!!busy || connecting}>
          {busy === "plain" && "Minting…"}
          {busy === "backed" && "Creating…"}
          {!busy && (backed ? "Create backed NFT" : "Create NFT")}
        </button>
      </form>

      {result && <div className="state-message">{result}</div>}
    </div>
  );
}
